import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, MagnifyingGlass } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import IdeaLabPromo from "@/components/IdeaLabPromo";
import { useSiteSettings } from "@/context/SiteSettingsContext";

// Real, live apps — these belong on /solutions, not in the concept catalogue.
const REAL_APP_SLUGS = new Set(["holborough-social", "action-management-portal", "reflective-mind", "golf-eclectic"]);

export default function DemoCatalogue() {
  const { settings } = useSiteSettings();
  const [demos, setDemos] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    api.get("/demos").then(({ data }) => {
      setDemos((data || []).filter((d) => !REAL_APP_SLUGS.has(d.slug)));
    }).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const set = new Set(demos.map((d) => d.category));
    return ["all", ...Array.from(set)];
  }, [demos]);

  const filtered = useMemo(() => {
    return demos.filter((d) => {
      const catMatch = category === "all" || d.category === category;
      const q = search.trim().toLowerCase();
      const searchMatch =
        !q ||
        d.title.toLowerCase().includes(q) ||
        d.short_description.toLowerCase().includes(q) ||
        (d.tags || []).some((t) => t.toLowerCase().includes(q));
      return catMatch && searchMatch;
    });
  }, [demos, search, category]);

  return (
    <div data-testid="demo-catalogue-page">
      <section className="border-b border-zinc-200">
        <div className="app-container py-20 md:py-28">
          <p className="overline mb-6">Concept lab</p>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-none max-w-4xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Concepts you can poke at.
          </h1>
          <p className="text-lg text-zinc-600 mt-6 max-w-2xl">
            These are interactive concepts — niche apps we'd love to co-build with a founding partner. They're working mock-ups, not finished products. Looking for our live, in-production work? <Link to="/solutions" className="underline underline-offset-4 hover:text-zinc-900">See solutions →</Link>
          </p>
          <div className="mt-10">
            <Link to="/guided-journey" className="btn-signal" data-testid="catalogue-guided-cta">
              <Compass size={18} weight="regular" /> Not sure which concept fits? Try the Concept Matcher
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-zinc-200 bg-white sticky top-[73px] z-30">
        <div className="app-container py-4 flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
          <div className="flex items-center gap-2 border border-zinc-200 bg-white px-3 py-2 w-full md:w-auto md:min-w-[320px]">
            <MagnifyingGlass size={18} className="text-zinc-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, feature, or tag"
              className="flex-1 outline-none text-sm bg-transparent"
              data-testid="catalogue-search"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-4 py-2 text-sm font-medium border transition-colors whitespace-nowrap ${
                  category === c ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                }`}
                data-testid={`catalogue-filter-${c.toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="app-container py-16 md:py-20">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-zinc-500 font-mono" data-testid="catalogue-empty">No demos matched. Try a different search.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((d) => (
                <Link
                  key={d.id}
                  to={`/demos/${d.slug}`}
                  className="border border-zinc-200 bg-white group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 relative"
                  data-testid={`catalogue-item-${d.slug}`}
                >
                  {d.demo_type && d.demo_type !== "case_study" && (
                    <span className="absolute top-3 left-3 z-10 text-[9px] font-mono uppercase tracking-widest bg-white border border-zinc-200 px-2 py-1 shadow-sm">
                      {d.demo_type === "interactive" && "★ Interactive"}
                      {d.demo_type === "mini_app" && "↳ Mini-app"}
                      {d.demo_type === "clickable" && "⌕ Clickable"}
                      {d.demo_type === "live" && "● Live app"}
                    </span>
                  )}
                  {d.co_innovation && settings.co_innovation_enabled && (
                    <span className="absolute top-3 right-3 z-10 text-[9px] font-mono uppercase tracking-widest bg-zinc-950 text-white px-2 py-1 shadow-sm" data-testid={`catalogue-coinnovation-${d.slug}`}>
                      ★ Co-innovation
                    </span>
                  )}
                  <div className="aspect-[4/3] bg-zinc-100 overflow-hidden border-b border-zinc-200">
                    {d.media_url && (
                      <img src={d.media_url} alt={d.media_alt || d.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    )}
                  </div>
                  <div className="p-6">
                    <p className="overline text-zinc-500 mb-2">{d.category}{d.client ? ` · ${d.client}` : ""}</p>
                    <h3 className="text-xl font-semibold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{d.title}</h3>
                    <p className="text-sm text-zinc-600 leading-relaxed">{d.short_description}</p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {(d.tags || []).slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border border-zinc-200 px-2 py-0.5">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <IdeaLabPromo variant="light" />
    </div>
  );
}
